import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { List, Input } from 'rsuite';
import { useSelector } from 'react-redux';
import {
  map,
  flow,
  getOr,
  reject,
  sortBy,
  sumBy,
  groupBy,
  isNull,
  debounce,
  filter
} from 'lodash/fp';
import excludeZeroLimit from '../../../helpers/excludeZeroLimit';
import guestQuantityLimit from '../../../helpers/guestQuantityLimit';
import { parseInt } from '../../../helpers/utils';
import { getViewReservationGuestInfo } from '../../../redux/Reservation/selectors/routes/view';
import PicklistCategory from '../PicklistCategory';

const PicklistWrapper = styled.div`
  padding: 5px;
  margin-bottom: 20px;
  border: 1px solid #efefef;
`;

const PicklistName = styled.h6`
  color: #fff;
  padding: 8px;
  text-align: center;
  text-transform: uppercase;
  background-color: #86cce7;
`;

const CategoryList = styled(List)`
  box-shadow: none;
  background: #efefef;
`;

const NotesSection = styled.div`
  padding: 20px 10px 10px;
  background: #f5f5f5;
  border-top: 1px solid #ddd;
`;

const NotesLabel = styled.p`
  text-align: left;
  margin-bottom: 6px;
  text-transform: uppercase;
`;

const Picklist = ({ picklist, order, onUpdateOrder }) => {
  const { id, name, trackingMethod, categories, items, limitRules } = picklist;
  const orderItems = getOr([], 'items', order);
  const defaultNotes = getOr('', 'notes', order);
  const guestInfo = useSelector(getViewReservationGuestInfo);
  const hasLimit = !isNull(limitRules);
  const quantityLimit = useMemo(
    () => guestQuantityLimit(guestInfo, limitRules),
    [guestInfo, limitRules]
  );

  const filteredCategories = useMemo(
    () =>
      flow(
        list => excludeZeroLimit(guestInfo, list),
        reject({ isVisible: false }),
        sortBy('position')
      )(categories),
    [categories]
  );
  const itemsByCategory = groupBy('categoryId', items);
  const totalOrderQuantity = useMemo(() => sumBy('quantity', orderItems), [
    orderItems
  ]);
  const picklistLimitReached = hasLimit && totalOrderQuantity >= quantityLimit;

  const getItemsForCategory = categoryId => {
    const categoryItems = flow(
      getOr([], categoryId),
      filter(
        ({ enabled = true, isVisible = 1 }) =>
          enabled && (isVisible === true || parseInt(isVisible) === 1)
      )
    )(itemsByCategory);
    return excludeZeroLimit(guestInfo, categoryItems);
  };

  const handleChangeNotes = value => {
    onUpdateOrder({ ...order, notes: value });
  };

  const debounceChangeNotes = debounce(1000)(params => {
    handleChangeNotes(params);
  });

  const handleChangeOrderItems = value => {
    onUpdateOrder({ ...order, items: value });
  };

  return (
    <PicklistWrapper>
      <PicklistName>
        {trackingMethod.label} - {name}
        {hasLimit && ` (${quantityLimit - totalOrderQuantity})`}
      </PicklistName>
      <CategoryList>
        {map(
          category => (
            <PicklistCategory
              data={category}
              key={`category-${category.id}`}
              guestInfo={guestInfo}
              picklistId={id}
              items={getItemsForCategory(category.id)}
              orderItems={getOr([], 'items', order)}
              picklistLimit={quantityLimit}
              picklistLimitReached={picklistLimitReached}
              onChangeOrderItems={handleChangeOrderItems}
            />
          ),
          filteredCategories
        )}
      </CategoryList>
      <NotesSection>
        <NotesLabel>{picklist.notes}</NotesLabel>
        <Input
          rows={3}
          defaultValue={defaultNotes}
          componentClass="textarea"
          onChange={debounceChangeNotes}
        />
      </NotesSection>
    </PicklistWrapper>
  );
};

Picklist.propTypes = {
  picklist: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    notes: PropTypes.string.isRequired,
    trackingMethod: PropTypes.shape({
      id: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired
    }).isRequired,
    categories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    limitRules: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  order: PropTypes.shape({
    notes: PropTypes.string.isRequired
  }),
  onUpdateOrder: PropTypes.func.isRequired
};

Picklist.defaultProps = {
  order: null
};

export default Picklist;
